import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    AumBreadcrumbComponentItem,
    Constants,
    EMPTY_ARRAY_LENGTH,
    IInvitationDialogData,
    IPageHeader,
    InviteUsersFailureDialogActions,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    NotificationType,
} from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { cloneDeep } from 'lodash-es';
import { InviteUserComponent } from '../../../../../../aumico-lib-users-fe/src/lib/pages/organization-users/invite-user/invite-user.component';
import {
    GetUserDto,
    OrganizationProvider,
    inviteUsers,
    removeUsers,
} from '../../../../../../aumico-lib-users-fe/src/public-api';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../pages/base.page';

@UntilDestroy(this)
@Component({
    selector: 'app-page-organization-users.page',
    templateUrl: './organization-users.page.html',
    styleUrls: ['./organization-users.page.scss'],
})
export class OrganizationUsersPage extends BasePageComponent implements OnInit {
    users: GetUserDto[] = [];

    readonly pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.ORGANIZATION_USERS,
    };
    actionText = 'aumico_new_button';

    breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: 'Your Organization',
            translated: true,
        },
        {
            displayName: '',
            translated: true,
        },
    ];

    showAddUserDialog: boolean;

    invitationDialogData: IInvitationDialogData;
    emailsToInvite: string[] = [];
    preparedEmail: string | null = null;
    invitationRequestIsOnGoing: boolean;
    isEmailInputValid = false;

    @ViewChild('inviteUser') inviteUserComponent: InviteUserComponent;

    constructor(injector: Injector, dialog: MatDialog, private organizationProvider: OrganizationProvider) {
        super(injector, dialog);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.organizationProvider
            .getUsers()
            .pipe(untilDestroyed(this))
            .subscribe((users) => this.handleUsers(users));

        this.organizationProvider.dispatchGetUsers();

        this.actionsListener$
            .pipe(ofType(removeUsers.success), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(removeUsers.failure), untilDestroyed(this))
            .subscribe((response) => this.handleErrors(response.payload));

        this.actionsListener$.pipe(ofType(inviteUsers.success), untilDestroyed(this)).subscribe(({ payload }) => {
            this.showAddUserDialog = false;
            this.invitationRequestIsOnGoing = false;

            if (this.hasFailedEmails(payload.failedInvitations)) {
                this.onHasFailedMails(payload.failedInvitations);
            } else {
                this.fireSuccessNotification({
                    titleKey: 'aumico_reports_notification_success_title',
                    descriptionKey: 'aumico_reports_invite_users_success_message',
                });
            }
        });

        this.actionsListener$
            .pipe(ofType(inviteUsers.failure, inviteUsers.resendinvitationfailure), untilDestroyed(this))
            .subscribe(() => {
                this.invitationRequestIsOnGoing = false;
                this.fireErrorNotification('aumico_reports_invite_users_wrong_data_error_message');
            });

        this.actionsListener$.pipe(ofType(inviteUsers.resendinvitationsuccess), untilDestroyed(this)).subscribe(() => {
            this.fireSuccessNotification({
                titleKey: 'aumico_reports_notification_success_title',
                descriptionKey: 'aumico_reports_invite_users_success_message',
            });
        });
        this.organizationProvider
            .getCurrentOrganizationName$()
            .pipe(untilDestroyed(this))
            .subscribe((name: string) => {
                if (this.breadcrumbItems.length > 1) {
                    this.breadcrumbItems[1].displayName = name;
                }
            });
    }

    public addNewUser(): void {
        this.openInviteNewUsersDialog();
    }

    protected fireErrorNotification(errorMessageKey: string): void {
        this.aumicoCoreProvider.dispatchAddNotification(
            'aumico_reports_notification_error_title',
            errorMessageKey,
            NotificationType.ERROR,
        );
    }
    onSaveAndInviteClick(): void {
        const emailsToSend =
            this.emailsToInvite.length > 0 ? this.emailsToInvite : this.preparedEmail ? [this.preparedEmail] : [];
        if (emailsToSend.length > 0) {
            this.organizationProvider.dispatchInviteUsers(emailsToSend);
            this.invitationRequestIsOnGoing = true;
        }
    }

    onEmailAdded(emails: string[]): void {
        this.emailsToInvite = emails;
        this.preparedEmail = null; // Clear prepared email when an email is added to the list
    }

    onPreparedEmail(email: string | null): void {
        this.preparedEmail = email;
    }

    onInputValidityChange(isValid: boolean): void {
        this.isEmailInputValid = isValid;
    }

    private handleUsers(users: GetUserDto[]): void {
        this.users = cloneDeep<GetUserDto[]>(users);
    }

    private hasFailedEmails(failedMails: string[]): boolean {
        return failedMails.length !== EMPTY_ARRAY_LENGTH;
    }

    public openInviteNewUsersDialog(): void {
        this.invitationDialogData = this.getInviteUsersDialogData();
        this.showAddUserDialog = true;
    }

    private onSaveSuccessfully(): void {
        this.fireSuccessNotification({
            titleKey: 'aumico_reports_notification_success_title',
            descriptionKey: 'aumico_reports_remove_users_success_message',
        });
    }

    private getInviteUsersDialogData(): IInvitationDialogData {
        return {
            titleLocalizationKey: 'aumico_reports_invite_new_users_dialog_title',
            contentMessage: 'aumico_users_organization_users_table_invite_modal_body',
            emailInputLabelKey: 'aumico_reports_invite_new_users_dialog_input_label',
            emailInputPlaceholderKey: 'aumico_reports_invite_new_users_dialog_input_placeholder',
            actionBtnTextKey: 'aumico_reports_invite_new_users_dialog_save_button',
        };
    }

    private handleErrors(error: HttpErrorResponse): void {
        let errorMessageKey: string | undefined;
        switch (error.status) {
            case 400:
                errorMessageKey = 'aumico_reports_remove_users_user_removing_self_error_message';
                break;
            case 500:
                errorMessageKey = Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG;
                break;
            default:
                errorMessageKey = Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG;
        }

        this.fireErrorNotification(errorMessageKey);
    }

    private onHasFailedMails(failedMails: string[]): void {
        let emailsExistingInOtherOrganizationsText = '';
        let emailsExistingInSameOrganizationText = '';
        failedMails.forEach((email) => {
            if (this.emailsToInvite.includes(email)) {
                emailsExistingInSameOrganizationText = emailsExistingInSameOrganizationText.concat(email).concat('\n');
            } else {
                emailsExistingInOtherOrganizationsText = emailsExistingInOtherOrganizationsText
                    .concat(email)
                    .concat('\n');
            }
        });

        const contentTexts = [];
        if (emailsExistingInOtherOrganizationsText !== '') {
            contentTexts.push('aumico_reports_invite_users_email_already_exist_error_message');
            contentTexts.push(emailsExistingInOtherOrganizationsText);
        }

        if (emailsExistingInSameOrganizationText !== '') {
            contentTexts.push('aumico_reports_invite_users_cannot_invite_same_organization_users');
            contentTexts.push(emailsExistingInSameOrganizationText);
        }

        this.dialog.open<ModalComponent, ModalData, InviteUsersFailureDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.WARNING,
                titleLocalizationKey: Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG,
                footerButtons: [
                    {
                        localizationKey: 'aumico_reports_invite_users_failure_okay_button_label',
                        action: InviteUsersFailureDialogActions.OKAY,
                        disabled: false,
                        buttonColor: 'primary',
                    },
                ],
                content: {
                    text: contentTexts,
                },
            },
        });
    }
}
